import Fuse from 'fuse.js';
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import {validateObject, validateField} from '../lib/validators';
import type {SearchOptions} from "../@types/entity";
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import {genUUID} from "../lib/picos/uuids";
import type {EntityBase} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Status = {
    Create: "C",
    Update: "U",
    Delete: "D"
};
export type StatusType = typeof Status[keyof typeof Status];
export type FatalError = {message: string, status: number, recoverable: boolean};
class Base implements EntityBase {
    firmId: uuid;
    addedBy: uuid;
    addedOn: number;
    readonly createdBy?: uuid | null;
    createdOn?: Date | null;
    dbAddedOn?: number | null;
    status: StatusType;
    entityId: uuid;
    entityType: string;
    importId?: uuid | null;
    importSource?: string | null;
    importedId?: string | null;
    allowTouch: boolean;
    shallow: boolean;
    fresh: boolean;
    persisted: boolean;
    fatalError?: FatalError | null;
    errorShown: boolean;
    restore: boolean;
    validationError?: string | null;
    waitingToSave: boolean;
    constructor(doc: typeof Base | any) {
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
        };
        // @ts-ignore
        this.firmId = doc._firmId || doc.firmId || doc.firmid || getCurrentFirmId();
        // @ts-ignore
        this.addedBy = doc._addedBy || doc.addedBy || doc.addedby || getCurrentUserId();
        // @ts-ignore
        this.addedOn = util.parseFloat(doc._addedOn) || util.parseFloat(doc.addedOn) || util.parseFloat(doc.addedon) || newHRTimestamp();
        // @ts-ignore
        this.createdBy = doc._createdBy || doc.createdBy || doc.createdby || null;
        // @ts-ignore
        this.createdOn = DateUtil.timestampOnly(doc._createdOn) || DateUtil.timestampOnly(doc.createdOn) || DateUtil.timestampOnly(doc.createdon) || null;
        // @ts-ignore
        this.dbAddedOn = util.parseFloat(doc._dbAddedOn) || util.parseFloat(doc.dbAddedOn) || util.parseFloat(doc.dbaddedon) || null;
        // @ts-ignore
        this.status = util.trimRight(doc._status) || util.trimRight(doc.status) || "C";
        // @ts-ignore
        this.importId = doc._importId || doc.importId || doc.importid || null;
        // @ts-ignore
        this.importSource = doc._importSource || doc.importSource || doc.importsource || null;
        // @ts-ignore
        this.importedId = doc._importedId || doc.importedId || doc.importedid || null;
        // @ts-ignore
        this.allowTouch = util.defaultBoolean([doc.allowTouch, doc._allowTouch, doc.allowtouch, false]);
        // @ts-ignore
        this.shallow = util.defaultBoolean([doc.shallow, doc._shallow, doc.shallow, true]);
        // @ts-ignore
        this.fresh = util.defaultBoolean([doc.fresh, doc._fresh, doc.fresh, true]);
        // @ts-ignore
        this.persisted = util.defaultBoolean([doc.persisted, doc._persisted, doc.persisted, false]);
        // @ts-ignore
        this.fatalError = doc._fatalError || doc.fatalError || doc.fatalerror || {};
        // @ts-ignore
        this.errorShown = util.defaultBoolean([doc.errorShown, doc._errorShown, doc.errorshown, false]);
        // @ts-ignore
        this.restore = util.defaultBoolean([doc.restore, doc._restore, doc.restore, false]);
        // @ts-ignore
        this.validationError = doc._validationError || doc.validationError || doc.validationerror || null;
        // @ts-ignore
        this.waitingToSave = util.defaultBoolean([doc.waitingToSave, doc._waitingToSave, doc.waitingtosave, false]);
    }
    /**
     * Schema-derived field metadata, memoized per entity class. Inside a static
     * method "this" is the concrete subclass, so getSchema() resolves to that
     * subclass's merged schema. Shared by the inherited search()/toJSON()/
     * clone()/isTransientProperty() implementations below so those bodies live
     * in exactly one place instead of in every entity.
     */
    /** Transient secret fields the client holds but never sends or stores; a generated entity overrides this. */
    static clientOnlySecretFields: string[] = [];

    private static _fieldMeta = new Map<Function, {fieldNames: string[]; searchFields: string[]; dateFields: string[]; arrayFields: string[]; transientFields: string[]; secretTransientFields: string[];}>();

    protected static fieldMeta() {
        let meta = Base._fieldMeta.get(this);
        if (!meta) {
            const fields = this.getSchema().fields as Record<string, SchemaField>;
            const fieldNames = Object.keys(fields);
            meta = {
                fieldNames,
                // Searchable single-string fields (mirrors generator getStringFields()).
                searchFields: fieldNames.filter(n => {
                    const f = fields[n];
                    const searchable = Object.prototype.hasOwnProperty.call(f, 'searchable') ? f.searchable : true;
                    return !!searchable && f.type === 'string';
                }),
                dateFields: fieldNames.filter(n => fields[n].type === 'date'),
                arrayFields: fieldNames.filter(n => fields[n].type.endsWith('[]')),
                transientFields: fieldNames.filter(n => fields[n].transient === true),
                // Transient secrets that exist only in the client (a document's signed uri). The browser
                // schema carries no `secret` attribute, so the generated entity class names them.
                secretTransientFields: [...this.clientOnlySecretFields]
            };
            Base._fieldMeta.set(this, meta);
        }
        return meta;
    }

    /**
     * Merge the shared base-entity fields into an entity's own schema. The base
     * field definitions live here once instead of being embedded in every
     * generated getSchema(). Base fields come first to preserve field order.
     *
     * OWA-491: also injects a shared 'fatalError' index into every entity so
     * items that failed to sync can be enumerated by type (the queue and the
     * Home dashboard "Needs Attention" card) in O(errors) instead of scanning
     * whole slices. Skipped when the schema already declares one (matter/task
     * declared it before it was hoisted here).
     */
    static withBaseFields(schema: EntitySchema): EntitySchema {
        const indexes = schema.indexes ?? [];
        const withFatalError = indexes.some(i => i.name === 'fatalError')
            ? indexes
            : [...indexes, {name: 'fatalError', fn: (e: any) => e.fatalError && (e.fatalError.message || e.fatalError.status)}];
        return {...schema, fields: {...Base.getSchema().fields, ...schema.fields}, indexes: withFatalError};
    }

    /**
     * Validates the object, given the constraints.  Throws a ValidationError if a constraint is violated, else return true.
     *
     * @param {string | null} [field] - optional field, otherwise validates the entire object
     * @throws {ValidationError}
     */
    validate(field?: string): boolean {
        const schema = (this.constructor as typeof Base).getSchema();
        if (field) return validateField(field, schema, this);
        else return validateObject(schema, this);
    }

    /**
     * @returns {Object} The item represented as JSON
     */
    toJSON(): object {
        const {fieldNames, dateFields, secretTransientFields} = (this.constructor as typeof Base).fieldMeta();
        const json: Record<string, any> = {};
        for (const f of fieldNames) {
            if (secretTransientFields.includes(f)) continue;
            // @ts-ignore
            json[f] = dateFields.includes(f) ? DateUtil.toLocalDateString(this[f]) : this[f];
        }
        return json;
    }

    get [Symbol.toStringTag]() {
        return this.toJSON();
    }

    /**
     * Clone the entire entity, shallow-copying any array fields so the copy can
     * be mutated independently of the original.
     */
    clone(): this {
        const ctor = this.constructor as new (doc: any) => this;
        const cloned = new ctor({...this});
        for (const f of (this.constructor as typeof Base).fieldMeta().arrayFields) {
            // @ts-ignore
            if (this[f]) cloned[f] = [...this[f]];
        }
        return cloned;
    }

    /**
     * Update timestamp.  This sets the addedOn and status fields.
     */
    touch() {
        if (!this.allowTouch || this.fresh) return;

        this.addedOn = newHRTimestamp();
        this.persisted = false;
        if (!this.fresh && this.status === 'C') this.status = 'U';
    }

    /**
     * Used by util.deepCompare() to sort objects based on their addedOn date
     *
     * @returns {number}
     */
    compare(a: Base, b: Base): number {
        // @ts-ignore
        return a.addedOn - b.addedOn;
    }

    /**
     * Tests if this is a transient field, i.e. not stored in the database
     *
     * @param {string} prop - The property to test
     * @returns {boolean} true if transient, false otherwise
     */
    isTransientProperty(prop: string): boolean {
        return (this.constructor as typeof Base).fieldMeta().transientFields.includes(prop);
    }

    /**
     * Quick and easy whole-object validation
     * @returns {boolean}
     */
    isValid(): boolean {
        this.validationError = null;

        try {
            this.validate();
            return true;
         }
        catch(err) {
            if (err instanceof Error) this.validationError = err.message;
            else if (typeof (err) === 'string') this.validationError = err;
            else this.validationError = JSON.stringify(err);

            return false;
        }
    }

    /**
     * Search all text and linked ref arrays for search terms.  Note that arrays of UUIDs that are NOT ref arrays
     * can not be searched from here, as this class does not store the full info.  You will need to search those
     * in the container rather than in here.
     *
     * For speed purposes, we look for the fastest possible rejection path.
     *
     * @param {string|string[]} termOrTerms - Either a search term or an array of search terms
     * @param {Object} options - Search options
     * @param {boolean} options.caseSensitive - Should the search be case-sensitive?  Default is false.
     * @param {boolean} options.matchAll - Do all search terms need to be matched? Default is true.
     * @param {boolean} options.includeSnippets - Should the results include snippets of the match? Default is false.
     * @param {boolean} options.matchReferences - Should reference arrays be searched? Default is true.
     * @param {boolean} options.fuzzy - Use fuzzy search?
     * @return {boolean}
     */
    search(termOrTerms: string | string[], options: SearchOptions): boolean {
        // If no terms, everything matches
        if (!termOrTerms) return true;

        // If a single term, move into an array for easier processing. Empty terms (from stray spaces) are not terms.
        let terms: string[] = (Array.isArray(termOrTerms) ? termOrTerms : [termOrTerms]).filter(t => !!t);
        if (terms.length === 0) return true;

        // Parse options.  Ignore includeSnippets for now.
        const caseSensitive = options && (Object.prototype.hasOwnProperty.call(options, 'caseSensitive')) ? options.caseSensitive : false;
        const matchAll = options && (Object.prototype.hasOwnProperty.call(options, 'matchAll')) ? options.matchAll : true;
        const fuzzy = options && (Object.prototype.hasOwnProperty.call(options, 'fuzzy')) ? options.fuzzy : false;
        const wholeWords = options && (Object.prototype.hasOwnProperty.call(options, 'wholeWords')) ? options.wholeWords : false;
        // const includeSnippets = options && (Object.prototype.hasOwnProperty.call(options, 'includeSnippets')) ? options.includeSnippets : false;

        // Get a list of fields to search using metadata from entity schema (string fields only!)
        const fields: string[] = (this.constructor as typeof Base).fieldMeta().searchFields;


        // If not case-sensitive, make all search terms lowercase
        if (!caseSensitive) {
            terms = terms.map(t => t.toLowerCase());
        }

        // Get a list of all the field values, with or without case thunking
        let values: string[] = [];

        // Get the actual field values, optionally with case thunking
        // A null or undefined field has no text. String(null) would be the word "null", which searches would then find.
        const fieldText = (f: string): string => {
            const v = this[f as keyof this];
            return v == null ? '' : String(v);
        };
        if (caseSensitive) values = fields.map(fieldText);
        else values = fields.map(f => fieldText(f).toLowerCase());

        const checkTerm = (term: string): boolean => {
            if (wholeWords) return values.some(value => !!value && util.matchWholeWord(term, value));
            else return values.some(value => !!value && value.includes(term));
        };

        // Handle normal (non-fuzzy) matches
        if (!fuzzy) {
            // Check every term
            let match = matchAll
                ? terms.every(term => checkTerm(term))
                : terms.some(term => checkTerm(term));

            if (match) return true;
        }
        else { // Fuzzy search

            // ignoreLocation: by default Fuse scores a match by how far it is from the start of the field, so
            // a misspelled word deep in a long note would never be found.
            const fuseOptions = {
                keys: fields,
                isCaseSensitive: caseSensitive,
                threshold: 0.4,
                ignoreLocation: true
            };

            const fuse = new Fuse([this], fuseOptions);

            let match = matchAll
                ? terms.every(term => fuse.search(term).length > 0)
                : terms.some(term => fuse.search(term).length > 0);

            if (match) return true;
        }

        return false;
    }

    static getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    static getNonRefArrayFields(): string[] { return []; }
    static getDefaultEntries(): Base[] {
        return [];
    }
    static getSchema(): EntitySchema {
        return {
    entity: 'base',
    comment: 'Base schema for all entities',
    table: '',

    fields: {
        firmId: {
            type: 'uuid',
            required: true,
            pk: true,
            base: true,
            default: '__FIRM__',
            comment: 'Associated firm'
        },
        addedBy: {
            type: 'uuid',
            required: true,
            base: true,
            default: '__USER__',
            comment: 'Who added this entry'
        },
        addedOn: {
            type: 'hrtimestamp',
            required: true,
            minValue: 1000000000000000,
            base: true,
            default: '__NOW__',
            comment: 'When was the entry added by the user?  This is a high-res timestamp, not a normal timestamp'
        },
        createdBy: {
            type: 'uuid',
            entityType: 'user',
            base: true,
            readOnly: 'privateMethod',
            comment: 'Original creator of this entity'
        },
        createdOn: {
            type: 'timestamp',
            default: '__NOW__',
            base: true,
            readOnly: 'dbDefaultOnce',
            comment: 'Original creation date for this item'
        },
        dbAddedOn: {
            type: 'hrtimestamp',
            minValue: 1000000000000000,
            base: true,
            readOnly: 'dbDefault',
            default: '__NOW__',
            comment: 'When was the entry added by the database?  This is a high-res timestamp, not a normal timestamp'
        },
        status: {
            type: 'char',
            maxLength: 1,
            values: ['C', 'U', 'D'],
            valueNames: ['Create', 'Update', 'Delete'],
            default: 'C',
            required: true,
            base: true,
            comment: 'Created, Updated or Deleted'
        },

        // Parent-Child relationships
        entityId: {
            type: 'uuid',
            required: true,
            comment: 'The related entity'
        },

        entityType: {
            type: 'string',
            required: true,
            searchable: false,
            comment: 'The schema type that owns this record'
        },

        // Importing
        // TODO: This is interesting to know, in case we want to wipe out the results of an import, but this only
        //       handles cases where there was no merge!
        importId: {
            type: 'uuid',
            entityType: 'importExport',
            searchable: false,
            comment: 'The id of the ImportExport'
        },

        importSource: {
            type: 'string',
            searchable: false,
            comment: 'Where this data came from, e.g. Bill4Time'
        },

        // TODO: Right now we are using the $id convention, where an additional field is added only during the import.
        // The issue is that when we merge records, we lose track of which ID's this really came from
        importedId: {
            type: 'string',
            transient: true,
            searchable: false,
            comment: 'The original ID of the entity from the import source'
        },

        // Transient fields
        allowTouch: {
            type: 'boolean',
            transient: true,
            comment: 'When updating a non-transient field, update the addedOn/addedBy?'
        },

        shallow: {
            type: 'boolean',
            default: true,
            transient: true,
            comment: 'Is this a shallow copy?'
        },

        fresh: {
            type: 'boolean',
            default: true,
            transient: true,
            comment: 'Is the entity fresh? (never saved to db?)'
        },

        persisted: {
            type: 'boolean',
            default: false,
            transient: true,
            comment: 'Is this entry persisted to the database?'
        },

        // Error structure will be something like { errorCode: 501, message: 'Failed to save', ... }
        fatalError: {
            type: 'json',
            jsdoc: '{message: string, status: number, recoverable: boolean}',
            default: null,
            transient: true,
            comment: 'Error saving or processing entity'
        },

        errorShown: {
            type: 'boolean',
            transient: true,
            comment: 'Has the fatal error been shown to the user yet?'
        },

        restore: {
            type: 'boolean',
            transient: true,
            comment: 'Are we doing a restore?'
        },

        validationError: {
            type: 'string',
            transient: true,
            searchable: false,
            comment: 'Message for validation error'
        },

        waitingToSave: {
            type: 'boolean',
            transient: true,
            comment: 'Is this entry in the process of saving?'
        }
    }
};
    }
    /**
     * Create a duplicate of this entity with its own unique base fields.
     *
     * @param {string} fieldName - name of the field to suffix with '(dup)'
     */
    duplicate(fieldName: string): this {
        // Copy only persistent fields — transient/runtime state (fresh, persisted,
        // shallow, dirty/save flags, etc.) must NOT carry into a duplicate.
        const _src: Record<string, any> = {};
        for (const _k of Object.keys(this)) {
            if (!this.isTransientProperty(_k)) _src[_k] = (this as any)[_k];
        }
        // An entity's own id field is its <entity>Id (e.g. taskId) — derived from the
        // schema's class name, NOT the mutable entityType (which points at the parent).
        // The duplicate gets a fresh id.
        const entityName = (this.constructor as typeof Base).getSchema().entity;
        const idField = entityName !== 'base' ? entityName + 'Id' : null;
        const ctor = this.constructor as new (doc: any) => this;
        const dup = new ctor({
            ..._src,
            ...(idField ? {[idField]: genUUID()} : {}),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            // The duplicate is a new entity: the original's creator and creation time are not its own
            createdBy: null,
            createdOn: null,
            dbAddedOn: null,
            status: 'C',
            importId: null,
            importSource: null,
            fresh: true,
            persisted: false
        });

        if (fieldName) {
            // @ts-ignore
            dup[fieldName] = dup[fieldName] + ' (dup)';
        }

        return dup;

    }
}
export default Base;
